import React, {
  useMemo,
} from 'react';
import {
  useFormContext,
  useWatch,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import sumBy from 'lodash/sumBy';
import noop from 'lodash/noop';
import groupBy from 'lodash/groupBy';
import omit from 'lodash/omit';
import values from 'lodash/values';
import Controller from 'core/components/FormController';
import Accordion from 'core/components/Accordion';
import CurrencyTextField from 'core/components/CurrencyTextField';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import {
  ClaimDataForm,
} from 'pages/Dashboard/pages/Billing/types';
import ServicesLookup from 'views/EMR/components/ServiceLookup';
import {
  ServiceCodeDetails,
  VisitNoteBillingCode,
} from 'views/EMR/components/ServiceLookup/type';
import {
  normalizeAssessmentCodeOption,
  normalizeServiceCodeOptionForClaim,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/utils/helper';
import {
  serviceCodesFormKeys,
} from 'views/EMR/VisitNoteForm';
import {
  useServiceFacilities,
} from 'views/EMR/stores/serviceFacility';
import {
  useGetPatientAuthorizationsByInsurance,
} from 'views/EMR/components/Authorizations/hooks';
import {
  getClaimAuthorizationOptionModels,
} from 'views/EMR/components/Authorizations/utils';

type Props = {
  claimId: number;
  referencedClaimId?: number;
  serviceCodeDefaults?: ServiceCodeDetails;
  amountPaid?: number | null;
};

function getVisitNoteBillingCodes(value: unknown): VisitNoteBillingCode[] {
  return Array.isArray(value) ? value as VisitNoteBillingCode[] : [];
}

export default function ClaimCharges({
  claimId,
  referencedClaimId = 0,
  serviceCodeDefaults = {},
  amountPaid,
}: Props) {
  const {
    control,
    watch,
  } = useFormContext<ClaimDataForm>();

  const [
    serviceCodes,
    facilityId = 0,
  ] = watch(['billingCodes', 'serviceFacility.serviceFacilityId']);
  const insuranceId = watch('insurance.insuranceId') ?? 0;
  const { data: patientAuthorizations = [] } = useGetPatientAuthorizationsByInsurance(insuranceId);
  const claimAuthorizationOptions = useMemo(
    () => getClaimAuthorizationOptionModels(patientAuthorizations),
    [patientAuthorizations],
  );

  const assessmentCodes = useWatch({ control, name: 'assessmentCodes' }) ?? [];

  const serviceFacilities = useServiceFacilities();

  const defaultPOS = useMemo(() => (
    facilityId === 0
      ? '12'
      : (serviceFacilities ?? [])
        .find(({ serviceFacilityId }) => serviceFacilityId === facilityId)?.defaultPOS
  ), [facilityId, serviceFacilities]);

  const totalCharges = useMemo(() => (
    sumBy(serviceCodes, (item) => (
      item?.serviceCodeDetails?.primaryClaimId === claimId
      || item?.serviceCodeDetails?.primaryClaimId === referencedClaimId
        ? Number(item.serviceCodeDetails?.billedAmount ?? '0') ?? 0
        : 0
    ))
  ), [serviceCodes]);

  const isPrimary = referencedClaimId === 0 || isNil(referencedClaimId);

  return (
    <Accordion
      defaultExpanded
      header="Charges"
      disableGutters
      headerClasses={{
        root: '!px-2 !py-1 !min-h-[initial] !bg-gray-100 !text-gray-600 !font-semibold text-sm',
        content: '!m-0',
      }}
      detailsClasses={{ root: '!p-2' }}
    >
      <div className="flex flex-col space-y-4 text-sm populate-form">
        <Controller
          name="assessmentCodes"
          control={control}
          render={({ field }) => (
            <ServicesLookup
              value={field.value ?? []}
              onChange={(value) => field.onChange(value ?? [])}
              type="ICDCode"
              label="Diagnosis or Nature of Illness or Injury (Box 21)"
              dropdownId="icd-code-selection"
              id="assessmentCodes-new"
              placeholder="Search diagnosis"
              classes={{
                root: 'w-full',
                table: 'w-[800px]',
                label: 'text-xs text-gray-500',
              }}
              className="!gap-2"
              normalizeOption={normalizeAssessmentCodeOption}
            />
          )}
        />
        <Controller
          name="billingCodes"
          control={control}
          render={({ field }) => {
            const groupedServiceCodes = groupBy(
              getVisitNoteBillingCodes(field.value),
              (serviceCode) => serviceCode?.serviceCodeDetails?.primaryClaimId,
            );

            return (
              <>
                <ServicesLookup
                  type="CPTCode"
                  formKeys={serviceCodesFormKeys}
                  label="Billing Codes"
                  dropdownId="cpt-code-selection"
                  id="billingCodes"
                  isClaim
                  placeholder="Search Billing Codes"
                  assessmentCodes={assessmentCodes}
                  claimAuthorizationOptions={claimAuthorizationOptions}
                  value={(isPrimary
                    ? groupedServiceCodes[claimId]
                    : groupedServiceCodes[referencedClaimId]
                  )}
                  visitNoteId={1}
                  patientId={1}
                  allowDuplicate
                  onChange={(_value, ...event) => {
                    const billingCodes = (_value ?? []) as VisitNoteBillingCode[];
                    field.onChange([
                      ...billingCodes,
                      ...values(omit(
                        groupedServiceCodes,
                        isPrimary ? claimId : referencedClaimId,
                      )).flat(),
                    ], ...event);
                  }}
                  normalizeOption={(option) => normalizeServiceCodeOptionForClaim(
                    option,
                    defaultPOS,
                    serviceCodeDefaults,
                  )}
                  classes={{
                    root: 'w-full',
                    table: 'w-full',
                    label: 'text-xs !text-gray-500',
                  }}
                  actions={[]}
                />
                <div className="flex items-center space-x-4 w-1/2 !min-w-[320px]">
                  <Wrapper
                    id="total-charge"
                    label="Total Charge (Box 28)"
                    className="w-1/3 pointer-events-none"
                    labelClasses="text-xs !text-populate-gray-1 mb-0.5"
                  >
                    <CurrencyTextField
                      id="total-charge"
                      onChange={noop}
                      value={totalCharges}
                      className="!bg-gray-100"
                    />
                  </Wrapper>
                  <Wrapper
                    id="total-amount-paid"
                    label="Total Amount Paid (Box 29)"
                    className="w-1/3 pointer-events-none"
                    labelClasses="text-xs !text-populate-gray-1 mb-0.5"
                  >
                    <CurrencyTextField
                      id="amount-paid"
                      onChange={noop}
                      value={amountPaid ?? ''}
                      className="!bg-gray-100"
                      nullIfEmpty
                    />
                  </Wrapper>
                </div>
              </>
            );
          }}
        />
      </div>
    </Accordion>
  );
}
